import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as ArchiveIcon,
  EditOutlined as EditIcon,
  InboxOutlined as InboxIcon,
  Search as SearchIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import LabelChips from '../common/LabelChips';
import LoadingSkeleton from '../LoadingSkeleton';
import PaginationControl from '../PaginationControl';
import { SAUDI_CITIES } from '../../constants/saudiCities';
import { APP_SHAPE, elevatedPanelSx } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useConfirm } from '../../hooks/useConfirm';
import { useOrganizationStore } from '../../stores/organization.store';
import { useRouteStore } from '../../stores/route.store';
import type { Route, RouteStatus } from '../../types/route.type';
import { getRouteColor } from '../../utils/routeColors';

const PAGE_SIZE = 20;

export default function RoutesListTab() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const {
    routes,
    total,
    isLoading,
    error,
    clearError,
    fetchRoutes,
    archiveRoute,
  } = useRouteStore();
  const { canMutate } = useAuthPermissions();
  const { confirm, confirmDialog } = useConfirm();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [status, setStatus] = useState<'' | Exclude<RouteStatus, 'archived'>>('');

  const orgId = selectedOrganization?.id;
  const canCreate = Boolean(orgId && canMutate(`recyclee.${orgId}.*.route.create`));
  const canEdit = Boolean(orgId && canMutate(`recyclee.${orgId}.*.route.write`));
  const canArchive = Boolean(orgId && canMutate(`recyclee.${orgId}.*.route.delete`));

  useEffect(() => {
    if (!orgId) return;
    void fetchRoutes({
      organization_id: orgId,
      page,
      limit: PAGE_SIZE,
      search: search.trim() || undefined,
      city: city || undefined,
      status: status || undefined,
    });
  }, [orgId, page, search, city, status, fetchRoutes]);

  useEffect(() => setPage(1), [orgId, search, city, status]);

  const hasActiveFilters = Boolean(search || city || status);
  const clearFilters = () => {
    setSearch('');
    setCity('');
    setStatus('');
  };

  const number = useMemo(() => new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }), []);

  if (!selectedOrganization) {
    return <Alert severity="info">{t('transportation.routes.selectOrganization')}</Alert>;
  }

  const archive = async (route: Route) => {
    const confirmed = await confirm({
      title: t('common.archive'),
      description: t('transportation.routes.archiveConfirm', { name: route.name }),
      tone: 'danger',
    });
    if (confirmed) await archiveRoute(selectedOrganization.id, route.id);
  };

  return (
    <Box>
      {error && <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: hasActiveFilters ? '2fr 1fr 1fr auto' : '2fr 1fr 1fr' },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            label={t('transportation.routes.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${APP_SHAPE.radiusSm}px` } }}
          />
          <FormControl size="small">
            <InputLabel>{t('transportation.routes.form.city')}</InputLabel>
            <Select label={t('transportation.routes.form.city')} value={city} onChange={(event) => setCity(event.target.value)}>
              <MenuItem value="">{t('transportation.routes.allCities')}</MenuItem>
              {SAUDI_CITIES.map((cityName) => <MenuItem key={cityName} value={cityName}>{cityName}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>{t('transportation.routes.form.status')}</InputLabel>
            <Select label={t('transportation.routes.form.status')} value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
              <MenuItem value="">{t('transportation.routes.allStatuses')}</MenuItem>
              <MenuItem value="active">{t('transportation.routes.status.active')}</MenuItem>
            </Select>
          </FormControl>
          {hasActiveFilters && (
            <Button size="small" onClick={clearFilters} sx={{ whiteSpace: 'nowrap' }}>{t('suppliers.clearFilters')}</Button>
          )}
        </Box>
      </Paper>

      {isLoading ? <LoadingSkeleton variant="table" filters={false} columns={7} rows={6} /> : (
        <Paper elevation={2} sx={elevatedPanelSx}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
            <Table stickyHeader sx={{ '& .MuiTableCell-head': { py: 1.5, px: 2 }, '& .MuiTableCell-body': { py: 1.75, px: 2 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('transportation.routes.columns.name')}</TableCell>
                  <TableCell>{t('transportation.routes.form.city')}</TableCell>
                  <TableCell align="right">{t('transportation.routes.columns.stops')}</TableCell>
                  <TableCell align="right">{t('transportation.routes.columns.distance')}</TableCell>
                  <TableCell>{t('transportation.routes.columns.truck')}</TableCell>
                  <TableCell>{t('suppliers.collectionPoints.table.labels')}</TableCell>
                  <TableCell align="right">{t('suppliers.table.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {routes.map((route) => (
                  <TableRow key={route.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: getRouteColor(route.id), flexShrink: 0 }} />
                        <Typography fontWeight={700}>{route.name}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{route.city}</TableCell>
                    <TableCell align="right">{route.stop_count}</TableCell>
                    <TableCell align="right">{number.format(route.estimated_distance_km)} km</TableCell>
                    <TableCell>
                      {route.assigned_truck ? <Chip size="small" label={route.assigned_truck.name} /> : '—'}
                    </TableCell>
                    <TableCell><LabelChips labels={route.labels} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('suppliers.table.view')}>
                        <IconButton aria-label={`${t('suppliers.table.view')} ${route.name}`} size="small" onClick={() => navigate(`/transportation/routes/${route.id}`)} sx={{ color: 'text.secondary', width: 40, height: 40 }}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title={t('suppliers.table.edit')}>
                          <IconButton aria-label={`${t('suppliers.table.edit')} ${route.name}`} size="small" onClick={() => navigate(`/transportation/routes/${route.id}/edit`)} sx={{ color: 'primary.main', width: 40, height: 40 }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canArchive && (
                        <Tooltip title={t('suppliers.table.archive')}>
                          <IconButton aria-label={`${t('suppliers.table.archive')} ${route.name}`} size="small" color="error" onClick={() => void archive(route)} sx={{ width: 40, height: 40 }}>
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {routes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 8, border: 0 }}>
                      <Stack alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 64, height: 64, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha(theme.palette.text.secondary, 0.08), color: 'text.secondary' }}>
                          <InboxIcon fontSize="large" />
                        </Box>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {hasActiveFilters ? t('transportation.routes.filteredTitle') : t('transportation.routes.emptyTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                          {hasActiveFilters ? t('transportation.routes.filteredHint') : t('transportation.routes.emptyHint')}
                        </Typography>
                        {!hasActiveFilters && canCreate && (
                          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => navigate('/transportation/routes/new')}>
                            {t('transportation.routes.addRoute')}
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ px: 2.5, pb: 2.5 }}>
            <PaginationControl page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />
          </Box>
        </Paper>
      )}

      {confirmDialog}
    </Box>
  );
}
